import { useSyncExternalStore } from 'react';

/**
 * Breakpoint thresholds (must match responsive.css).
 */
const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
  /**
   * Narrow desktop. Above this the sidebar can afford to stay expanded; below
   * it, 240px of nav on a 1366px laptop leaves too little for grids that run
   * edge to edge. Standard guidance for dense enterprise layouts is to
   * auto-collapse around here.
   */
  narrowDesktop: 1280,
  /**
   * A touch screen whose short side is this or less is a phone, whichever way
   * it is held. A phone in landscape is 844px wide, past `mobile`, but only
   * about 390px tall; the smallest tablet's short side is 744px.
   */
  phoneShortSide: 500,
} as const;

const COARSE = '(pointer: coarse)';

/**
 * Subscribes to every query a flag reads. Each flag listens to its own
 * breakpoint: listening to one query for all of them left `isMobile` stale
 * when a phone rotated across 768px, until something else re-rendered.
 */
function makeSubscribe(...queries: string[]) {
  return (callback: () => void) => {
    const mqls = queries.map((q) => window.matchMedia(q));
    for (const mql of mqls) mql.addEventListener('change', callback);
    return () => {
      for (const mql of mqls) mql.removeEventListener('change', callback);
    };
  };
}

const maxWidth = (px: number) => `(max-width: ${px}px)`;

const subscribeMobile = makeSubscribe(maxWidth(BREAKPOINTS.mobile));
const subscribeTablet = makeSubscribe(maxWidth(BREAKPOINTS.tablet));
const subscribeNarrow = makeSubscribe(maxWidth(BREAKPOINTS.narrowDesktop));
const subscribePhone = makeSubscribe(
  maxWidth(BREAKPOINTS.mobile),
  COARSE,
  `(max-height: ${BREAKPOINTS.phoneShortSide}px)`,
);

function getIsMobile() {
  return window.innerWidth <= BREAKPOINTS.mobile;
}

function getIsTablet() {
  return window.innerWidth <= BREAKPOINTS.tablet;
}

function getIsNarrowDesktop() {
  return window.innerWidth <= BREAKPOINTS.narrowDesktop;
}

function getIsPhone() {
  if (getIsMobile()) return true;
  const shortSide = Math.min(window.innerWidth, window.innerHeight);
  return shortSide <= BREAKPOINTS.phoneShortSide && window.matchMedia(COARSE).matches;
}

/**
 * Returns reactive `isMobile` (<=768px), `isTablet` (<=1024px) and
 * `isNarrowDesktop` (<=1280px) flags, plus `isPhone`: `isMobile`, or a touch
 * screen whose short side is 500px or less (a phone held sideways).
 * Uses `useSyncExternalStore` for tear-free reads during concurrent rendering.
 *
 * Usage:
 *   const { isMobile, isTablet, isNarrowDesktop, isPhone } = useIsMobile();
 */
export function useIsMobile() {
  const isMobile = useSyncExternalStore(subscribeMobile, getIsMobile, () => false);
  const isTablet = useSyncExternalStore(subscribeTablet, getIsTablet, () => false);
  const isNarrowDesktop = useSyncExternalStore(subscribeNarrow, getIsNarrowDesktop, () => false);
  const isPhone = useSyncExternalStore(subscribePhone, getIsPhone, () => false);
  return { isMobile, isTablet, isNarrowDesktop, isPhone };
}
